import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Badge } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { UserContext } from "../../contexts/UserContext";
import { podeUsarChat, temChatMaster } from "../../utils";
import { chatService } from "../../services/resources/chatService";
import { conectarChat } from "../../services/resources/chatSocket";
import { openNotification } from "../Notification";
import { ChatPainel } from "./ChatPainel";
import { useChatContatos } from "./useChatContatos";
import {
  carregarFixadas,
  desafixarConversaChat,
  desafixarMensagemChat,
  fixarConversaChat,
  fixarMensagemChat,
} from "./chatFixacao";
import {
  codigoConversaDoEvento,
  conversaAposEvento,
  conversaEstaAtiva,
  deveSinalizarNovaMensagem,
  marcarMensagemFixada,
  mesclarConversa,
  mesclarFixada,
  mesclarMensagem,
  removerFixada,
  somaNaoLidas,
} from "./chatFormat";
import "./chat.css";

export const ChatHost = () => {
  const { user } = useContext(UserContext);
  const autorizado = podeUsarChat(user);
  const master = temChatMaster(user);
  const [aberto, setAberto] = useState(false);
  const [vista, setVista] = useState("lista");
  const [conversas, setConversas] = useState([]);
  const [filtroLista, setFiltroLista] = useState("");
  const [conversaAtual, setConversaAtual] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [fixadas, setFixadas] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const conversaAtualRef = useRef(null);
  const abertoRef = useRef(false);
  const vistaRef = useRef("lista");
  const contatosEstado = useChatContatos(autorizado, vista);

  const marcarPainelAberto = (valor) => {
    abertoRef.current = valor;
    setAberto(valor);
  };

  const marcarVista = (valor) => {
    vistaRef.current = valor;
    setVista(valor);
  };

  const marcarConversaAtual = (conversa) => {
    conversaAtualRef.current = conversa;
    setConversaAtual(conversa);
  };

  const carregarConversas = useCallback(async () => {
    try {
      const { data } = await chatService.listarConversas();
      setConversas(Array.isArray(data) ? data : []);
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível carregar o chat</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    }
  }, []);

  const marcarLida = useCallback(async (codigo) => {
    try {
      await chatService.marcarComoLida(codigo);
      setConversas((atual) =>
        atual.map((item) =>
          String(item.codigo) === String(codigo) ? { ...item, naoLidas: 0 } : item
        )
      );
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível marcar a conversa como lida</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    }
  }, []);

  const abrirConversa = useCallback(
    async (conversa) => {
      marcarPainelAberto(true);
      marcarVista("thread");
      marcarConversaAtual(conversa);
      setTexto("");
      setMensagens([]);
      setFixadas([]);
      try {
        const [{ data }] = await Promise.all([
          chatService.listarMensagens(conversa.codigo, { page: 0, size: 50 }),
          carregarFixadas(conversa.codigo, setFixadas),
        ]);
        setMensagens([...(data?.content || [])].reverse());
        await marcarLida(conversa.codigo);
      } catch (error) {
        openNotification(
          "error",
          <strong>Não foi possível abrir a conversa</strong>,
          error?.response?.data?.userMessage || error?.response?.data?.detail
        );
      }
    },
    [marcarLida]
  );

  useEffect(() => {
    if (!autorizado) return undefined;
    carregarConversas();
    const desconectar = conectarChat((evento) => {
      const codigoConversa = codigoConversaDoEvento(evento);
      const ativa = conversaEstaAtiva(
        abertoRef.current,
        vistaRef.current,
        conversaAtualRef.current?.codigo,
        codigoConversa
      );
      if (evento?.tipo === "FIXADA" && evento.mensagem && ativa) {
        setFixadas((lista) => mesclarFixada(lista, evento.mensagem));
        setMensagens((lista) =>
          marcarMensagemFixada(lista, evento.mensagem.codigo, true)
        );
        return;
      }
      if (evento?.tipo === "DESAFIXADA" && evento.mensagem && ativa) {
        setFixadas((lista) => removerFixada(lista, evento.mensagem));
        setMensagens((lista) =>
          marcarMensagemFixada(lista, evento.mensagem.codigo, false)
        );
        return;
      }
      if (evento?.tipo !== "MENSAGEM" || !evento.mensagem) return;
      if (evento.conversa) {
        setConversas((atual) =>
          mesclarConversa(atual, conversaAposEvento(evento.conversa, ativa))
        );
      }
      if (ativa) {
        setMensagens((lista) => mesclarMensagem(lista, evento.mensagem));
        marcarLida(codigoConversa);
        return;
      }
      if (
        deveSinalizarNovaMensagem(
          ativa,
          evento.mensagem.autorCodigo,
          user?.codigo_usuario
        )
      ) {
        openNotification(
          "info",
          <strong>{evento.mensagem.autorNome || "Nova mensagem"}</strong>,
          evento.mensagem.conteudo,
          3
        );
      }
    }, carregarConversas);
    return desconectar;
  }, [autorizado, carregarConversas, marcarLida, user?.codigo_usuario]);

  if (!autorizado) {
    return null;
  }

  async function selecionarContato(contato) {
    try {
      const { data } = await chatService.abrirOuCriar(contato.codigo);
      setConversas((atual) => mesclarConversa(atual, data));
      await abrirConversa(data);
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível iniciar a conversa</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    }
  }

  async function enviar() {
    const conteudo = texto.trim();
    if (!conversaAtual?.codigo || enviando || !conteudo) return;
    setEnviando(true);
    try {
      const { data } = await chatService.enviar(conversaAtual.codigo, conteudo);
      setMensagens((lista) => mesclarMensagem(lista, data));
      setTexto("");
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível enviar a mensagem</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    } finally {
      setEnviando(false);
    }
  }

  function voltar() {
    marcarVista("lista");
    marcarConversaAtual(null);
    setMensagens([]);
    setFixadas([]);
    setTexto("");
    carregarConversas();
  }

  function fecharPainel() {
    marcarPainelAberto(false);
  }

  function abrirPainel() {
    marcarPainelAberto(true);
    marcarVista("lista");
    marcarConversaAtual(null);
    carregarConversas();
  }

  return (
    <div className="chat-host">
      {aberto ? (
        <ChatPainel
          user={user}
          master={master}
          vista={vista}
          conversas={conversas}
          filtroLista={filtroLista}
          conversaAtual={conversaAtual}
          mensagens={mensagens}
          fixadas={fixadas}
          texto={texto}
          enviando={enviando}
          contatos={contatosEstado.contatos}
          instituicoes={contatosEstado.instituicoes}
          buscaContato={contatosEstado.buscaContato}
          instituicaoContato={contatosEstado.instituicaoContato}
          carregandoContatos={contatosEstado.carregandoContatos}
          onFechar={fecharPainel}
          onVoltar={voltar}
          onNova={() => {
            marcarVista("nova");
            contatosEstado.resetar();
          }}
          onLiberacoes={() => marcarVista("liberacoes")}
          onFiltrarLista={setFiltroLista}
          onAbrirConversa={abrirConversa}
          onFixarConversa={(conversa) => fixarConversaChat(conversa, setConversas)}
          onDesafixarConversa={(conversa) => desafixarConversaChat(conversa, setConversas)}
          onTexto={setTexto}
          onEnviar={enviar}
          onFixar={(mensagem) =>
            fixarMensagemChat(conversaAtual?.codigo, mensagem, setFixadas, setMensagens)
          }
          onDesafixar={(mensagem) =>
            desafixarMensagemChat(conversaAtual?.codigo, mensagem, setFixadas, setMensagens)
          }
          onBuscarContato={contatosEstado.setBuscaContato}
          onInstituicaoContato={contatosEstado.setInstituicaoContato}
          onSelecionarContato={selecionarContato}
        />
      ) : (
        <Badge count={somaNaoLidas(conversas)} overflowCount={99} offset={[-4, 4]}>
          <button
            type="button"
            className="chat-fab"
            onClick={abrirPainel}
            aria-label="Abrir chat"
            title="Chat"
          >
            <MessageOutlined />
          </button>
        </Badge>
      )}
    </div>
  );
};
